import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Gift, PackageCheck, Hourglass, Ban, Trash2, Download, Eye, Copy, Check, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const GIFT_PASS_URL = `${window.location.origin}/gift-pass`;

const STATUS_LABELS = { requested: 'Awaiting handover', issued: 'Handed over', declined: 'Declined' };
const STATUS_BADGE = {
  requested: 'bg-amber-500/15 text-amber-600',
  issued: 'bg-green-500/15 text-green-600',
  declined: 'bg-red-500/15 text-red-600',
};

const fmtDate = (s) => (s ? new Date(s.length === 10 ? `${s}T00:00:00` : s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <div className="text-xs font-medium text-muted-foreground">{label}</div>
    <div className="text-sm whitespace-pre-wrap break-words">{children || '—'}</div>
  </div>
);

const PassDetail = ({ p }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Pass ID">{p.id}</Field>
      <Field label="Submitted">{new Date(p.created_at).toLocaleString('en-IN')}</Field>
      <Field label="Assigned Sales Person">
        {p.sales_user_id ? p.sales_user_name : `Unassigned — no Sales account matched "${p.sales_name}"`}
      </Field>
      <Field label="Status">
        {STATUS_LABELS[p.status]}
        {p.status_by_name && ` by ${p.status_by_name} on ${fmtDate(p.status_at)}`}
        {p.status_note && ` · ${p.status_note}`}
      </Field>
      <Field label="Centre">{p.centre} · {p.visit_type} site</Field>
      <Field label="Visit date">{fmtDate(p.visit_date)}</Field>
      <Field label="Client">{p.client_name}{p.designation ? ` (${p.designation})` : ''}</Field>
      <Field label="Company">{p.company}</Field>
      <Field label="Email">{p.email}</Field>
      <Field label="Phone">{p.phone}</Field>
      <Field label="People on the visit">{p.visitors}</Field>
      <Field label="Seats required">{p.seats}</Field>
      <Field label="Lead source">{p.lead_source}{p.broker_name ? ` (${p.broker_name})` : ''}</Field>
      <Field label="Tumblers">{p.tumblers}{p.broker_gift ? ' (incl. 1 for broker)' : ''}</Field>
    </div>
    <Field label="Client Database">
      {p.lead_created ? 'New lead created for the Sales Person' : 'Client was already in the Client Database — shared with the Sales Person'}
    </Field>
    {p.possible_duplicate_of && <Field label="Possible duplicate">Same company had Gift Pass {p.possible_duplicate_of} in the 30 days before this one.</Field>}
  </div>
);

const CSV_COLUMNS = [
  'id', 'created_at', 'visit_date', 'centre', 'visit_type', 'sales_user_name', 'client_name', 'designation', 'company',
  'email', 'phone', 'visitors', 'seats', 'lead_source', 'broker_name', 'tumblers', 'status', 'status_by_name', 'status_at',
  'status_note', 'possible_duplicate_of',
];

const toCsv = (rows) => {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [CSV_COLUMNS.join(','), ...rows.map((r) => CSV_COLUMNS.map((c) => esc(r[c])).join(','))].join('\n');
};

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <Card><CardContent className="p-4 flex items-center gap-3">
    <Icon className="w-5 h-5 text-primary shrink-0" />
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-bold">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  </CardContent></Card>
);

export const GiftPassTab = ({ api }) => {
  const [passes, setPasses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [salesPeople, setSalesPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [repFilter, setRepFilter] = useState('all');
  const [centreFilter, setCentreFilter] = useState('all');
  const [viewing, setViewing] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [passesRes, summaryRes, formRes] = await Promise.all([
        api.get('/gift-pass/requests'),
        api.get('/gift-pass/summary').catch(() => ({ data: null })),
        api.get('/gift-pass/sales-people').catch(() => ({ data: { sales_people: [] } })),
      ]);
      setPasses(passesRes.data.passes || []);
      setSummary(summaryRes.data);
      setSalesPeople(formRes.data.sales_people || []);
    } catch (err) {
      toast.error('Failed to load gift passes');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const centres = useMemo(() => [...new Set(passes.map((p) => p.centre).filter(Boolean))].sort(), [passes]);
  const reps = useMemo(() => {
    const m = new Map(salesPeople.map((s) => [s.id, s.name]));
    passes.forEach((p) => { if (p.sales_user_id && !m.has(p.sales_user_id)) m.set(p.sales_user_id, p.sales_user_name); });
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [passes, salesPeople]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return passes.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (repFilter === 'unassigned' ? p.sales_user_id : repFilter !== 'all' && p.sales_user_id !== repFilter) return false;
      if (centreFilter !== 'all' && p.centre !== centreFilter) return false;
      if (q && ![p.client_name, p.company, p.id, p.email, p.phone].some((v) => (v || '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [passes, search, statusFilter, repFilter, centreFilter]);

  const replacePass = (updated) => {
    setPasses((list) => list.map((p) => (p.id === updated.id ? updated : p)));
    setViewing((v) => (v && v.id === updated.id ? updated : v));
  };

  const setStatus = async (p, status) => {
    let note;
    if (status === 'declined') {
      note = window.prompt('Reason for declining (optional)');
      if (note === null) return;
    }
    try {
      const res = await api.patch(`/gift-pass/requests/${p.id}/status`, { status, note });
      replacePass(res.data);
      toast.success(status === 'issued' ? 'Marked as handed over' : status === 'declined' ? 'Gift declined' : 'Status reset');
      api.get('/gift-pass/summary').then((r) => setSummary(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update status');
    }
  };

  const reassign = async (p, salesUserId) => {
    if (salesUserId === p.sales_user_id) return;
    try {
      const res = await api.patch(`/gift-pass/requests/${p.id}/assign`, { sales_user_id: salesUserId });
      replacePass(res.data);
      toast.success(`Lead reassigned to ${res.data.sales_user_name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reassign');
    }
  };

  const deletePass = async (p) => {
    if (!window.confirm(`Delete Gift Pass ${p.id} for ${p.company}? The lead stays in the Client Database.`)) return;
    try {
      await api.delete(`/gift-pass/requests/${p.id}`);
      toast.success('Gift pass deleted');
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete gift pass');
    }
  };

  const exportCsv = () => {
    const blob = new Blob([toCsv(filtered)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `clayworks-gift-passes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(GIFT_PASS_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  if (loading) {
    return <div className="py-12 text-center text-muted-foreground">Loading gift passes…</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={Gift} label="Gift Passes" value={summary?.total ?? passes.length} />
        <StatCard icon={Hourglass} label="Awaiting handover" value={summary?.by_status?.requested ?? 0} sub={`${summary?.tumblers_pending ?? 0} tumblers`} />
        <StatCard icon={PackageCheck} label="Handed over" value={summary?.by_status?.issued ?? 0} sub={`${summary?.tumblers_issued ?? 0} tumblers`} />
        <StatCard icon={Ban} label="Declined" value={summary?.by_status?.declined ?? 0} />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-lg font-semibold">Gift Passes</h2>
          <p className="text-xs text-muted-foreground">Each submission is added to the selected Sales Person's Client Database for follow-up.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={copyLink} data-testid="mo-gift-pass-copy-link">
            {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />} Copy form link
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length} data-testid="mo-gift-pass-export">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client, company, pass ID, phone" className="w-full sm:w-72" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={repFilter} onValueChange={setRepFilter}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sales People</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={centreFilter} onValueChange={setCentreFilter}>
          <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All centres</SelectItem>
            {centres.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Visit</TableHead>
                <TableHead>Pass ID</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Centre</TableHead>
                <TableHead>Assigned to</TableHead>
                <TableHead>Tumblers</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  {passes.length ? 'No gift passes match these filters' : 'No gift passes yet'}
                </TableCell></TableRow>
              ) : filtered.map((p) => (
                <TableRow key={p.id} data-testid={`mo-gift-pass-row-${p.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">{fmtDate(p.visit_date)}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm font-mono">
                    {p.id}
                    {p.possible_duplicate_of && <span className="ml-1 text-xs text-amber-600" title={`Possible duplicate of ${p.possible_duplicate_of}`}>dup?</span>}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{p.company}</div>
                    <div className="text-xs text-muted-foreground">{p.client_name} · {p.phone}</div>
                  </TableCell>
                  <TableCell className="text-sm">{p.centre}<div className="text-xs text-muted-foreground">{p.visit_type}</div></TableCell>
                  <TableCell>
                    <Select value={p.sales_user_id || ''} onValueChange={(v) => reassign(p, v)}>
                      <SelectTrigger className={`w-40 h-8 text-sm ${p.sales_user_id ? '' : 'border-amber-500 text-amber-600'}`} data-testid={`mo-gift-pass-assign-${p.id}`}>
                        <SelectValue placeholder={`Unassigned (${p.sales_name || p.sales_user_name})`}>{p.sales_user_id ? p.sales_user_name : undefined}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {salesPeople.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>{p.tumblers}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${STATUS_BADGE[p.status] || ''}`}>{STATUS_LABELS[p.status] || p.status}</span>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {p.status === 'requested' ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setStatus(p, 'issued')} title="Mark handed over" data-testid={`mo-gift-pass-issue-${p.id}`}>
                          <PackageCheck className="w-3.5 h-3.5 text-green-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setStatus(p, 'declined')} title="Decline">
                          <Ban className="w-3.5 h-3.5 text-amber-600" />
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => setStatus(p, 'requested')} title="Undo — back to awaiting handover">
                        <Undo2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setViewing(p)} aria-label="View gift pass">
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deletePass(p)} aria-label="Delete gift pass">
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(open) => !open && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Gift Pass {viewing?.id}</DialogTitle></DialogHeader>
          {viewing && <PassDetail p={viewing} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
